<template>
    <v-container :class="embedded ? 'pa-0' : 'mb-12 mb-lg-0'">
        <v-overlay :absolute="embedded" :value="loading">
            <v-progress-circular indeterminate width="6" size="48"/>
        </v-overlay>

        <v-row v-if="!embedded" justify="center">
            <v-col cols="12" xl="11">
                <v-card>
                    <v-card-title class="primary secondary--text d-flex align-center" style="word-break: normal">
                        <v-icon color="secondary" class="mr-2">mdi-shield-lock</v-icon>
                        <span class="font-weight-bold">SEGURIDAD</span>
                        <v-spacer/>
                        <v-btn icon color="secondary" :loading="securityLoading" @click="getSecurity">
                            <v-icon>mdi-sync</v-icon>
                        </v-btn>
                    </v-card-title>
                </v-card>
            </v-col>
        </v-row>

        <v-card v-else outlined class="security-inline-heading mb-3">
            <v-card-title class="settings-panel-title">
                <v-icon color="primary" class="mr-2">mdi-shield-lock</v-icon>
                Seguridad
                <v-spacer/>
                <v-btn icon color="primary" :loading="securityLoading" @click="getSecurity">
                    <v-icon>mdi-sync</v-icon>
                </v-btn>
            </v-card-title>
        </v-card>

        <v-row :justify="embedded ? 'start' : 'center'">
            <v-col cols="12" :xl="embedded ? 12 : 11">
                <v-row dense>
                    <v-col v-for="item in securitySections" :key="item.key" cols="12" sm="6" md="4" :lg="embedded ? 4 : 2">
                        <v-card
                            outlined
                            :class="['security-section-card fill-height', {'security-section-card-active': securityDialog === item.key}]"
                            @click="openSecuritySection(item.key)">
                            <v-card-text class="security-section-content text-center pa-4">
                                <v-avatar :color="item.color" size="48" class="mb-3">
                                    <v-icon color="white">{{ item.icon }}</v-icon>
                                </v-avatar>
                                <div class="subtitle-2 font-weight-bold primary--text security-section-title">{{ item.title }}</div>
                                <v-chip x-small label :color="item.ok ? 'success' : 'warning'" dark class="mt-2">
                                    {{ item.status }}
                                </v-chip>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>

        <v-dialog
            v-model="securityDialogOpen"
            persistent
            scrollable
            max-width="920px"
            :fullscreen="$vuetify.breakpoint.xsOnly">
            <v-card class="security-fixed-dialog" outlined>
                <v-card-title class="primary secondary--text" style="word-break: normal">
                    <v-icon color="secondary" class="mr-2">{{ selectedSecuritySection.icon }}</v-icon>
                    {{ selectedSecuritySection.title }}
                    <v-spacer/>
                    <v-btn icon color="secondary" @click="closeSecuritySection">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text class="security-fixed-dialog-body pa-4">
                    <section v-if="securityDialog === 'account'">
                        <v-list two-line color="transparent" class="pa-0">
                            <v-list-item>
                                <v-list-item-icon><v-icon color="primary">mdi-badge-account</v-icon></v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>{{ security.account.nickname || accountTag }}</v-list-item-title>
                                    <v-list-item-subtitle>ID de wallet</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-icon><v-icon color="primary">mdi-email-check</v-icon></v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>{{ security.account.email || user.email }}</v-list-item-title>
                                    <v-list-item-subtitle>Correo registrado</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-icon><v-icon :color="accountStatusColor">mdi-check-decagram</v-icon></v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>{{ accountStatusLabel }}</v-list-item-title>
                                    <v-list-item-subtitle>Validacion de identidad</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-icon><v-icon :color="security.account.notifications ? 'success' : 'warning'">mdi-bell-ring</v-icon></v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>{{ security.account.notifications ? 'Activas' : 'Pendientes' }}</v-list-item-title>
                                    <v-list-item-subtitle>Notificaciones de seguridad</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-icon><v-icon color="primary">mdi-clock-check</v-icon></v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>{{ security.account.last_access || user.last_access || 'Sin registro' }}</v-list-item-title>
                                    <v-list-item-subtitle>Ultimo acceso</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </section>

                    <section v-else-if="securityDialog === 'password'">
                        <v-sheet outlined rounded class="pa-3 mb-4">
                            <div class="d-flex align-center">
                                <v-icon :color="passwordPolicy.expired ? 'warning' : 'primary'" class="mr-3">mdi-lock-clock</v-icon>
                                <div>
                                    <div class="subtitle-2 font-weight-bold">Control de contraseña</div>
                                    <div class="caption grey--text text--darken-1">
                                        Último cambio: {{ passwordPolicy.changed_at || 'Sin registro' }} · Vence: {{ passwordPolicy.expires_at || 'Sin programar' }}
                                    </div>
                                </div>
                            </div>
                            <v-alert v-if="passwordPolicy.expired" dense text type="warning" class="mt-3 mb-0">
                                Tu contraseña está vencida. Actualízala para mantener la cuenta protegida.
                            </v-alert>
                        </v-sheet>
                        <v-form ref="formPass" @submit.prevent="changePass" lazy-validation v-model="valid">
                            <v-row>
                                <v-col cols="12">
                                    <v-text-field
                                        :label="labels.old_password"
                                        name="old_password"
                                        v-model="form.old_password"
                                        :append-icon="passwordHidden ? 'mdi-eye-off' : 'mdi-eye'"
                                        @click:append="() => (passwordHidden = !passwordHidden)"
                                        :type="passwordHidden ? 'password' : 'text'"
                                        :error-messages="errors.old_password"
                                        :disabled="loading"
                                        :rules="[rules.req('old_password')]"
                                        @input="clearErrors('old_password')"
                                        prepend-icon="mdi-lock-check"
                                        filled/>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        :label="labels.password"
                                        name="password"
                                        v-model="form.password"
                                        :append-icon="newPasswordHidden ? 'mdi-eye-off' : 'mdi-eye'"
                                        @click:append="() => (newPasswordHidden = !newPasswordHidden)"
                                        :type="newPasswordHidden ? 'password' : 'text'"
                                        :error-messages="errors.password"
                                        :disabled="loading"
                                        :rules="[rules.req('password'), rules.min('password', 8, 'La')]"
                                        @input="clearErrors('password')"
                                        prepend-icon="mdi-lock-plus"
                                        filled/>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        :label="labels.password_confirmation"
                                        name="password_confirmation"
                                        v-model="form.password_confirmation"
                                        :type="newPasswordHidden ? 'password' : 'text'"
                                        :error-messages="errors.password_confirmation"
                                        :disabled="loading"
                                        :rules="[rules.req('password_conf'), passwordConfirmationRule]"
                                        @input="clearErrors('password_confirmation')"
                                        prepend-icon="mdi-lock-check"
                                        filled/>
                                </v-col>
                            </v-row>
                            <v-progress-linear :value="passwordProgress" :color="passwordStrengthColor" height="8" rounded class="mb-3"/>
                            <div class="caption font-weight-bold mb-2" :class="passwordStrengthColor + '--text'">
                                {{ passwordStrengthText }}
                            </div>
                            <v-row dense>
                                <v-col cols="12" sm="6" v-for="item in passwordChecks" :key="item.text">
                                    <v-chip small label :color="item.valid ? 'success' : 'grey lighten-2'" :text-color="item.valid ? 'white' : 'grey darken-2'">
                                        <v-icon left small>{{ item.valid ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                                        {{ item.text }}
                                    </v-chip>
                                </v-col>
                            </v-row>
                            <v-btn type="submit" :loading="loading" :disabled="loading || !valid || !passwordsMatch" color="primary" large block class="mt-5">
                                <v-icon left>mdi-lock-check</v-icon>Actualizar contraseña
                            </v-btn>
                        </v-form>
                    </section>

                    <section v-else-if="securityDialog === 'two_factor'">
                        <v-row dense>
                            <v-col v-for="item in twoFactorOptions" :key="item.key" cols="12" md="6">
                                <v-sheet outlined rounded class="pa-3 fill-height two-factor-option">
                                    <div class="d-flex align-center mb-2">
                                        <v-avatar size="38" :color="item.active ? 'success' : 'primary'" class="mr-3">
                                            <v-icon color="white">{{ item.icon }}</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="subtitle-2 font-weight-bold">{{ item.title }}</div>
                                            <v-chip x-small label :color="item.active ? 'success' : item.color" dark>
                                                {{ item.status }}
                                            </v-chip>
                                        </div>
                                    </div>
                                    <div class="body-2 grey--text text--darken-1">{{ item.detail }}</div>
                                </v-sheet>
                            </v-col>
                        </v-row>

                        <v-row dense class="mt-4">
                            <v-col cols="12" sm="6">
                                <v-btn
                                    color="primary"
                                    outlined
                                    block
                                    :loading="twoFactorLoading === 'email'"
                                    :disabled="securityLoading || twoFactorLoading !== null || twoFactorStatus.email"
                                    @click="enableEmailTwoFactor">
                                    <v-icon left>mdi-email-lock</v-icon>Activar correo
                                </v-btn>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-btn
                                    color="primary"
                                    block
                                    :outlined="!twoFactorStatus.authenticator"
                                    :loading="twoFactorLoading === 'authenticator'"
                                    :disabled="securityLoading || twoFactorLoading !== null"
                                    @click="setupAuthenticator">
                                    <v-icon left>mdi-cellphone-key</v-icon>{{ twoFactorStatus.authenticator ? 'Reconfigurar app' : 'Configurar app' }}
                                </v-btn>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-btn
                                    color="primary"
                                    outlined
                                    block
                                    :disabled="securityLoading || twoFactorLoading !== null || !passkeySupported"
                                    @click="openPasskeyDialog">
                                    <v-icon left>mdi-fingerprint</v-icon>Agregar llave
                                </v-btn>
                            </v-col>
                            <v-col cols="12" v-if="passkeys.length">
                                <v-list two-line color="transparent" class="pa-0 mt-2">
                                    <v-list-item v-for="item in passkeys" :key="'passkey-' + item.id" class="px-0">
                                        <v-list-item-avatar color="primary">
                                            <v-icon color="secondary">mdi-fingerprint</v-icon>
                                        </v-list-item-avatar>
                                        <v-list-item-content>
                                            <v-list-item-title class="font-weight-bold">{{ item.name }}</v-list-item-title>
                                            <v-list-item-subtitle>
                                                Último uso: {{ item.last_used_at || 'Sin uso' }} · Creada: {{ item.created_at || 'Sin fecha' }}
                                            </v-list-item-subtitle>
                                        </v-list-item-content>
                                        <v-list-item-action>
                                            <v-btn
                                                icon
                                                color="error"
                                                :loading="passkeyLoading === 'delete-' + item.id"
                                                :disabled="passkeyLoading !== null"
                                                @click="deletePasskey(item)">
                                                <v-icon>mdi-delete-outline</v-icon>
                                            </v-btn>
                                        </v-list-item-action>
                                    </v-list-item>
                                </v-list>
                            </v-col>
                            <v-col cols="12" v-if="twoFactorEnabled">
                                <v-btn color="error" text block :disabled="twoFactorLoading !== null" @click="disableTwoFactorDialog = true">
                                    <v-icon left>mdi-shield-off</v-icon>Desactivar verificación completa
                                </v-btn>
                            </v-col>
                        </v-row>
                    </section>

                    <section v-else-if="securityDialog === 'dynamic_key'">
                        <v-sheet
                            v-if="dynamicKey.enabled && dynamicKey.code"
                            outlined
                            rounded
                            role="button"
                            title="Copiar clave dinámica"
                            class="pa-4 dynamic-key-panel"
                            @click="copyDynamicKey(dynamicKey.code)">
                            <div class="d-flex align-center">
                                <v-icon color="primary" class="mr-3">mdi-cellphone-lock</v-icon>
                                <div>
                                    <div class="subtitle-2 font-weight-bold">Clave dinámica actual</div>
                                    <div class="caption grey--text text--darken-1">
                                        {{ dynamicKey.device_name || 'App Valzum' }}
                                    </div>
                                </div>
                                <v-spacer/>
                                <v-chip small label color="secondary" class="primary--text font-weight-bold">{{ dynamicKey.remaining }}s</v-chip>
                            </div>
                            <div class="text-h4 font-weight-bold text-center my-3 dynamic-key-code">{{ dynamicKey.code }}</div>
                            <v-progress-linear :value="dynamicKeyProgress" color="primary" height="6" rounded/>
                        </v-sheet>

                        <v-sheet v-else outlined rounded class="pa-4">
                            <div class="d-flex align-center">
                                <v-avatar color="primary" size="42" class="mr-3">
                                    <v-icon color="secondary">mdi-cellphone-lock</v-icon>
                                </v-avatar>
                                <div>
                                    <div class="subtitle-2 font-weight-bold">{{ twoFactorStatus.dynamic_key ? 'Clave activa' : 'Clave por activar' }}</div>
                                    <div class="body-2 grey--text text--darken-1">{{ dynamicKeySectionText }}</div>
                                </div>
                            </div>
                        </v-sheet>

                        <v-row dense class="mt-4">
                            <v-col cols="12" sm="6">
                                <v-btn
                                    color="primary"
                                    block
                                    :outlined="!dynamicKey.enabled"
                                    :loading="twoFactorLoading === 'dynamic-key'"
                                    :disabled="securityLoading || twoFactorLoading !== null || !nativeDynamicKeyDevice"
                                    @click="dynamicKey.enabled ? disableDynamicKey() : enableDynamicKey()">
                                    <v-icon left>mdi-cellphone-lock</v-icon>{{ dynamicKeyButtonText }}
                                </v-btn>
                            </v-col>
                            <v-col cols="12" sm="6" v-if="dynamicKey.enabled && dynamicKey.can_rotate">
                                <v-btn
                                    color="primary"
                                    outlined
                                    block
                                    :loading="twoFactorLoading === 'dynamic-key-rotate'"
                                    :disabled="securityLoading || twoFactorLoading !== null"
                                    @click="rotateDynamicKey">
                                    <v-icon left>mdi-refresh-circle</v-icon>Cambiar clave
                                </v-btn>
                            </v-col>
                        </v-row>
                    </section>

                    <section v-else-if="securityDialog === 'devices'">
                        <v-alert v-if="!security.devices.length" text type="info">{{ noDeviceText }}</v-alert>
                        <v-row dense>
                            <v-col v-for="item in security.devices" :key="item.id" cols="12" md="6">
                                <v-sheet outlined rounded class="pa-3 fill-height">
                                    <div class="d-flex align-start">
                                        <v-avatar color="primary" size="42" class="mr-3">
                                            <v-icon color="secondary">{{ item.device === 'ios' ? 'mdi-cellphone-iphone' : (item.device === 'android' ? 'mdi-cellphone' : 'mdi-monitor') }}</v-icon>
                                        </v-avatar>
                                        <div class="flex-grow-1">
                                            <div class="subtitle-2 font-weight-bold">{{ item.name }}</div>
                                            <div class="caption grey--text text--darken-1">{{ item.app }}</div>
                                            <div class="mt-2">
                                                <v-chip x-small label :color="deviceTrustColor(item)" dark class="mr-1 mb-1">
                                                    {{ deviceTrustLabel(item) }}
                                                </v-chip>
                                                <v-chip x-small label :color="item.push_enabled ? 'success' : 'grey'" dark class="mr-1 mb-1">
                                                    {{ item.push_enabled ? 'Notifica' : 'Sin avisos' }}
                                                </v-chip>
                                                <v-chip x-small label :color="item.dynamic_key_enabled ? 'primary' : 'grey'" dark class="mr-1 mb-1">
                                                    {{ item.dynamic_key_enabled ? 'Clave dinámica' : 'Sin clave' }}
                                                </v-chip>
                                                <v-chip v-if="item.dynamic_key_enabled" x-small label :color="item.dynamic_key_native_app ? 'success' : 'warning'" dark class="mr-1 mb-1">
                                                    {{ item.dynamic_key_native_app ? 'App Valzum' : 'No genera clave' }}
                                                </v-chip>
                                                <v-chip v-if="item.current" x-small label color="secondary" class="primary--text font-weight-bold mb-1">
                                                    Este dispositivo
                                                </v-chip>
                                            </div>
                                            <div class="caption mt-2">IP: {{ item.last_ip || 'Sin registro' }}</div>
                                            <div class="caption">Ultimo uso: {{ item.last_seen_at || 'Sin registro' }}</div>
                                        </div>
                                    </div>
                                    <v-btn
                                        small
                                        outlined
                                        color="error"
                                        block
                                        class="mt-3"
                                        :loading="revokingDeviceId === item.id"
                                        :disabled="revokingDeviceId !== null"
                                        @click="forgetDevice(item)"
                                        v-if="item.status === 'active'">
                                        <v-icon left small>mdi-close-circle</v-icon>Quitar dispositivo
                                    </v-btn>
                                </v-sheet>
                            </v-col>
                        </v-row>
                    </section>

                    <section v-else-if="securityDialog === 'support'">
                        <v-list dense color="transparent">
                            <v-list-item v-for="item in securityChecks" :key="item.text">
                                <v-list-item-icon>
                                    <v-icon :color="item.valid ? 'success' : 'warning'">{{ item.valid ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>{{ item.text }}</v-list-item-title>
                                    <v-list-item-subtitle>{{ item.detail }}</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                        <v-btn block color="primary" outlined to="/user/support/new" class="mt-4">
                            <v-icon left>mdi-face-agent</v-icon>Crear ticket
                        </v-btn>
                    </section>
                </v-card-text>
                <v-divider/>
                <v-card-actions class="security-fixed-dialog-footer">
                    <v-btn text color="primary" @click="closeSecuritySection">Cerrar</v-btn>
                    <v-spacer/>
                    <v-btn icon color="primary" :loading="securityLoading" @click="getSecurity">
                        <v-icon>mdi-sync</v-icon>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="authenticatorDialog" persistent scrollable max-width="520px">
            <v-card class="security-fixed-dialog">
                <v-card-title class="primary secondary--text" style="word-break: normal">
                    <v-icon color="secondary" class="mr-2">mdi-cellphone-key</v-icon>
                    App autenticadora
                    <v-spacer/>
                    <v-btn icon color="secondary" :disabled="twoFactorLoading === 'confirm-authenticator'" @click="authenticatorDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text class="security-fixed-dialog-body pt-4">
                    <v-alert dense text type="info">
                        Escanea el QR con Google Authenticator, Microsoft Authenticator, 1Password u otra app compatible.
                    </v-alert>
                    <div class="text-center my-4" v-if="authenticatorSetup.otpauth_uri">
                        <vue-qrcode :value="authenticatorSetup.otpauth_uri" :scale="7" :margin="1"/>
                    </div>
                    <v-text-field
                        v-model="authenticatorSetup.secret"
                        label="Clave manual"
                        prepend-icon="mdi-key-chain"
                        readonly
                        filled/>
                    <v-text-field
                        v-model="authenticatorSetup.code"
                        label="Codigo de 6 digitos"
                        inputmode="numeric"
                        autocomplete="one-time-code"
                        maxlength="6"
                        counter="6"
                        prepend-icon="mdi-numeric"
                        :error-messages="errors.code"
                        :disabled="twoFactorLoading === 'confirm-authenticator'"
                        @input="clearErrors('code')"
                        @keyup.enter="confirmAuthenticator"
                        filled/>
                </v-card-text>
                <v-divider/>
                <v-card-actions class="security-fixed-dialog-footer">
                    <v-btn text color="error" :disabled="twoFactorLoading === 'confirm-authenticator'" @click="authenticatorDialog = false">
                        Cancelar
                    </v-btn>
                    <v-spacer/>
                    <v-btn
                        color="primary"
                        :loading="twoFactorLoading === 'confirm-authenticator'"
                        :disabled="!authenticatorSetup.code"
                        @click="confirmAuthenticator">
                        <v-icon left>mdi-check-circle</v-icon>Confirmar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="passkeyDialog" persistent scrollable max-width="520px">
            <v-card class="security-fixed-dialog">
                <v-card-title class="primary secondary--text" style="word-break: normal">
                    <v-icon color="secondary" class="mr-2">mdi-fingerprint</v-icon>
                    Llave de acceso
                    <v-spacer/>
                    <v-btn icon color="secondary" :disabled="passkeyLoading === 'create'" @click="passkeyDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text class="security-fixed-dialog-body pt-4">
                    <v-alert dense text type="info">
                        Usa huella, Face ID, PIN del equipo o una llave fisica compatible para entrar sin escribir contraseña.
                    </v-alert>
                    <v-text-field
                        v-model="passkeyForm.name"
                        label="Nombre de la llave"
                        prepend-icon="mdi-form-textbox"
                        :disabled="passkeyLoading === 'create'"
                        filled/>
                    <v-text-field
                        v-model="passkeyForm.password"
                        label="Contraseña actual"
                        type="password"
                        prepend-icon="mdi-lock-check"
                        :error-messages="errors.password"
                        :disabled="passkeyLoading === 'create'"
                        @input="clearErrors('password')"
                        @keyup.enter="createPasskey"
                        filled/>
                </v-card-text>
                <v-divider/>
                <v-card-actions class="security-fixed-dialog-footer">
                    <v-btn text color="error" :disabled="passkeyLoading === 'create'" @click="passkeyDialog = false">
                        Cancelar
                    </v-btn>
                    <v-spacer/>
                    <v-btn
                        color="primary"
                        :loading="passkeyLoading === 'create'"
                        :disabled="!passkeyForm.password || !passkeySupported"
                        @click="createPasskey">
                        <v-icon left>mdi-check-circle</v-icon>Crear llave
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="disableTwoFactorDialog" persistent scrollable max-width="460px">
            <v-card class="security-fixed-dialog">
                <v-card-title class="primary secondary--text" style="word-break: normal">
                    <v-icon color="secondary" class="mr-2">mdi-shield-off</v-icon>
                    Desactivar verificación
                </v-card-title>
                <v-card-text class="security-fixed-dialog-body pt-4">
                    <v-alert dense text type="warning">
                        Para quitar la verificación en dos pasos, confirma tu contraseña actual.
                    </v-alert>
                    <v-text-field
                        v-model="disableTwoFactorForm.password"
                        label="Contraseña actual"
                        type="password"
                        prepend-icon="mdi-lock-check"
                        :disabled="twoFactorLoading === 'disable'"
                        @keyup.enter="disableTwoFactor"
                        filled/>
                </v-card-text>
                <v-divider/>
                <v-card-actions class="security-fixed-dialog-footer">
                    <v-btn text color="primary" :disabled="twoFactorLoading === 'disable'" @click="disableTwoFactorDialog = false">
                        Cancelar
                    </v-btn>
                    <v-spacer/>
                    <v-btn
                        color="error"
                        :loading="twoFactorLoading === 'disable'"
                        :disabled="!disableTwoFactorForm.password"
                        @click="disableTwoFactor">
                        Desactivar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
    import axios from 'axios';
    import Form from "../helpers/form";
    import {devicePayload} from "../helpers/device";
    import {createPasskey, isWebAuthnSupported} from "../helpers/webauthn";
    import VueQrcode from 'vue-qrcode';

    export default {
        name: "Password",
        components: { VueQrcode },
        mixins: [Form],
        props: {
            user: {
                type: Object,
                required: true
            },
            embedded: {
                type: Boolean,
                default: false
            }
        },
        data: () => ({
            passwordHidden: true,
            newPasswordHidden: true,
            securityLoading: false,
            twoFactorLoading: null,
            securityDialog: null,
            dynamicKeyLoading: false,
            dynamicKeyTimer: null,
            authenticatorDialog: false,
            passkeyDialog: false,
            passkeyLoading: null,
            passkeySupported: false,
            disableTwoFactorDialog: false,
            revokingDeviceId: null,
            security: {
                account: {
                    email: null,
                    phone: null,
                    nickname: null,
                    status: null,
                    last_access: null,
                    notifications: false,
                    two_factor: {
                        enabled: false,
                        email: false,
                        authenticator: false,
                        passkey: false,
                        passkeys: 0,
                        dynamic_key: false,
                        device_bound: false,
                        methods: []
                    },
                    dynamic_key: {
                        enabled: false,
                        code: null,
                        period: 30,
                        remaining: 0,
                        expires_at: null,
                        device_name: null,
                        app_required: false,
                        manual_entry: false,
                        can_rotate: false,
                        rotation_expires_at: null,
                        rotation_due: false
                    },
                    password: {
                        changed_at: null,
                        expires_at: null,
                        reminder_days: 90,
                        days_remaining: null,
                        expired: false,
                        history_count: 0
                    }
                },
                devices: []
            },
            dynamicKey: {
                enabled: false,
                code: null,
                period: 30,
                remaining: 0,
                expires_at: null,
                device_name: null,
                app_required: false,
                manual_entry: false,
                can_rotate: false,
                rotation_expires_at: null,
                rotation_due: false
            },
            passkeys: [],
            passkeyForm: {
                name: 'Mi dispositivo',
                password: null
            },
            authenticatorSetup: {
                secret: null,
                otpauth_uri: null,
                code: null
            },
            disableTwoFactorForm: {
                password: null
            },
            labels: {
                old_password: 'Contraseña Actual',
                password: 'Nueva Contraseña',
                password_conf: 'confirmación de la contraseña',
                password_confirmation: 'Confirmar Contraseña',
            },
            form: {
                old_password: null,
                password: null,
                password_confirmation: null,
            },
            deviceHeaders: [
                { text: 'Dispositivo', value: 'name' },
                { text: 'Estado', value: 'trusted', align: 'center' },
                { text: 'Notificaciones', value: 'push_enabled', align: 'center', sortable: false },
                { text: 'IP', value: 'last_ip' },
                { text: 'Ultimo uso', value: 'last_seen_at' },
                { text: 'Registrado', value: 'created_at' },
                { text: 'Accion', value: 'actions', align: 'center', sortable: false },
            ],
            footerProps: {
                itemsPerPageOptions: [10, 25, 50, -1]
            },
            noDeviceText: 'No tienes dispositivos registrados'
        }),
        computed: {
            accountTag() {
                return '$' + (this.user.nickname || (this.user.id ? String(this.user.id).padStart(5, '0') : ''));
            },
            accountStatusLabel() {
                if (this.security.account.status === 103 || this.user.status === 103) return 'Verificada';
                if (this.security.account.status === 102 || this.user.status === 102) return 'En revision';
                if (this.security.account.status === 104 || this.user.status === 104) return 'Bloqueada';
                return 'Sin verificar';
            },
            accountStatusColor() {
                if (this.security.account.status === 103 || this.user.status === 103) return 'success';
                if (this.security.account.status === 102 || this.user.status === 102) return 'info';
                if (this.security.account.status === 104 || this.user.status === 104) return 'error';
                return 'warning';
            },
            securityDialogOpen: {
                get() {
                    return !!this.securityDialog;
                },
                set(value) {
                    if (!value) {
                        this.closeSecuritySection();
                    }
                }
            },
            selectedSecuritySection() {
                return this.securitySections.find(item => item.key === this.securityDialog) || {
                    icon: 'mdi-shield-lock',
                    title: 'Seguridad'
                };
            },
            securitySections() {
                const sections = [
                    {
                        key: 'account',
                        icon: 'mdi-account-shield',
                        title: 'Cuenta',
                        color: this.accountStatusColor,
                        status: this.accountStatusLabel,
                        ok: this.security.account.status === 103 || this.user.status === 103
                    },
                    {
                        key: 'password',
                        icon: 'mdi-lock-reset',
                        title: 'Contraseña',
                        color: this.passwordPolicy.expired ? 'warning' : 'primary',
                        status: this.passwordPolicy.expired ? 'Vencida' : 'Vigente',
                        ok: !this.passwordPolicy.expired
                    },
                    {
                        key: 'two_factor',
                        icon: 'mdi-two-factor-authentication',
                        title: 'Verificación',
                        color: this.twoFactorEnabled ? 'success' : 'primary',
                        status: this.twoFactorEnabled ? 'Activa' : 'Pendiente',
                        ok: this.twoFactorEnabled
                    },
                    {
                        key: 'dynamic_key',
                        icon: 'mdi-cellphone-lock',
                        title: 'Clave dinámica',
                        color: this.twoFactorStatus.dynamic_key ? 'success' : 'primary',
                        status: this.twoFactorStatus.dynamic_key ? 'Activa' : 'Por activar',
                        ok: this.twoFactorStatus.dynamic_key
                    },
                    {
                        key: 'devices',
                        icon: 'mdi-cellphone-key',
                        title: 'Dispositivos',
                        color: this.activeDevices > 0 ? 'success' : 'primary',
                        status: this.activeDevices + ' activos',
                        ok: this.activeDevices > 0
                    },
                    {
                        key: 'support',
                        icon: 'mdi-face-agent',
                        title: 'Soporte',
                        color: 'primary',
                        status: 'Revisión',
                        ok: this.securityChecks.every(item => item.valid)
                    }
                ];

                if (this.embedded) {
                    return sections.filter(item => ['password', 'two_factor', 'dynamic_key', 'devices'].indexOf(item.key) !== -1);
                }

                return sections;
            },
            passwordChecks() {
                const value = this.form.password || '';

                return [
                    { text: '8 caracteres', valid: value.length >= 8 },
                    { text: 'Mayuscula y minuscula', valid: /[A-Z]/.test(value) && /[a-z]/.test(value) },
                    { text: 'Numero', valid: /[0-9]/.test(value) },
                    { text: 'Simbolo', valid: /[^A-Za-z0-9]/.test(value) },
                ];
            },
            passwordScore() {
                return this.passwordChecks.filter(item => item.valid).length;
            },
            passwordProgress() {
                return this.passwordScore * 25;
            },
            passwordStrengthColor() {
                if (!this.form.password) return 'grey';
                if (this.passwordScore <= 2) return 'error';
                if (this.passwordScore === 3) return 'warning';
                return 'success';
            },
            passwordStrengthText() {
                if (!this.form.password) return 'Escribe una nueva contraseña';
                if (this.passwordScore <= 2) return 'Contraseña debil';
                if (this.passwordScore === 3) return 'Contraseña aceptable';
                return 'Contraseña fuerte';
            },
            passwordsMatch() {
                return !this.form.password_confirmation || this.form.password === this.form.password_confirmation;
            },
            activeDevices() {
                return this.security.devices.filter(item => item.status === 'active').length;
            },
            trustedDynamicDevices() {
                return this.security.devices.filter(item => item.trusted && item.status === 'active' && item.dynamic_key_enabled).length;
            },
            twoFactorStatus() {
                return (this.security.account && this.security.account.two_factor) ? this.security.account.two_factor : {
                    enabled: false,
                    email: false,
                    authenticator: false,
                    passkey: false,
                    passkeys: 0,
                    dynamic_key: false,
                    device_bound: false,
                    methods: []
                };
            },
            twoFactorEnabled() {
                return !!this.twoFactorStatus.enabled;
            },
            passwordPolicy() {
                return (this.security.account && this.security.account.password) ? this.security.account.password : {
                    changed_at: null,
                    expires_at: null,
                    reminder_days: 90,
                    days_remaining: null,
                    expired: false,
                    history_count: 0
                };
            },
            nativeDynamicKeyDevice() {
                const device = this.devicePayload();
                const deviceName = String(device.device || '').toLowerCase();

                return device.name === 'Valzum'
                    && ['android', 'ios'].indexOf(deviceName) !== -1
                    && !!(device.device_uid || device.ios_id || device.fcm_token);
            },
            dynamicKeyButtonText() {
                if (this.dynamicKey.enabled) {
                    return 'Desactivar clave';
                }

                if (this.twoFactorStatus.dynamic_key && !this.nativeDynamicKeyDevice) {
                    return 'Activa en app';
                }

                return 'Activar clave';
            },
            dynamicKeySectionText() {
                if (!this.nativeDynamicKeyDevice) {
                    return this.twoFactorStatus.dynamic_key
                        ? 'Abre la app Valzum donde la configuraste para ver el codigo.'
                        : 'Se activa desde la app Valzum instalada en tu telefono.';
                }

                return this.twoFactorStatus.dynamic_key
                    ? 'Puedes ver el codigo en este dispositivo.'
                    : 'Activa esta clave en tu app Valzum.';
            },
            dynamicKeyProgress() {
                const period = Number(this.dynamicKey.period || 30);
                const remaining = Number(this.dynamicKey.remaining || 0);

                return period > 0 ? Math.max(0, Math.min(100, (remaining / period) * 100)) : 0;
            },
            twoFactorOptions() {
                return [
                    {
                        key: 'email',
                        icon: 'mdi-email-lock',
                        title: 'Codigo al correo',
                        status: this.twoFactorStatus.email ? 'Activo' : 'Disponible',
                        active: this.twoFactorStatus.email,
                        color: 'primary',
                        detail: 'Envia un codigo temporal al correo validado de la cuenta.'
                    },
                    {
                        key: 'authenticator',
                        icon: 'mdi-cellphone-key',
                        title: 'App autenticadora',
                        status: this.twoFactorStatus.authenticator ? 'Activa' : 'Recomendada',
                        active: this.twoFactorStatus.authenticator,
                        color: 'primary',
                        detail: 'Genera codigos de 30 segundos desde una app segura.'
                    },
                    {
                        key: 'passkey',
                        icon: 'mdi-fingerprint',
                        title: 'Llave de acceso',
                        status: this.twoFactorStatus.passkey ? this.twoFactorStatus.passkeys + ' activa(s)' : 'Disponible',
                        active: this.twoFactorStatus.passkey,
                        color: 'primary',
                        detail: 'Permite ingresar con huella, Face ID, PIN del equipo o llave fisica.'
                    }
                ];
            },
            securityChecks() {
                return [
                    {
                        text: 'Identidad',
                        detail: this.accountStatusLabel,
                        valid: this.security.account.status === 103 || this.user.status === 103
                    },
                    {
                        text: 'Notificaciones',
                        detail: this.security.account.notifications ? 'Activadas' : 'Sin dispositivo activo',
                        valid: this.security.account.notifications
                    },
                    {
                        text: 'Dispositivos',
                        detail: this.trustedDynamicDevices + ' con clave dinámica',
                        valid: this.trustedDynamicDevices > 0
                    },
                    {
                        text: 'Verificación en dos pasos',
                        detail: this.twoFactorEnabled ? 'Activa' : 'Pendiente',
                        valid: this.twoFactorEnabled
                    },
                    {
                        text: 'Contraseña',
                        detail: this.passwordPolicy.expired
                            ? 'Vencida'
                            : (this.passwordPolicy.expires_at ? 'Programada hasta ' + this.passwordPolicy.expires_at : 'Sin fecha programada'),
                        valid: !this.passwordPolicy.expired
                    }
                ];
            }
        },
        created () {
            this.$emit('success');
            this.passkeySupported = isWebAuthnSupported();
            if (!this.errors.code) {
                this.$set(this.errors, 'code', []);
            }
            this.getSecurity();
            this.loadPasskeys();
        },
        beforeDestroy() {
            this.stopDynamicKeyTimer();
        },
        methods: {
            devicePayload() {
                return devicePayload();
            },
            copyDynamicKey(value) {
                if (!value) return;

                this.copyToClipboard(String(value));
            },
            copyToClipboard(value) {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(value).then(() => {
                        this.$toast.success('Clave copiada.', {icon: 'mdi-content-copy'});
                    }).catch(() => {
                        this.copyToClipboardFallback(value);
                    });
                    return;
                }

                this.copyToClipboardFallback(value);
            },
            copyToClipboardFallback(value) {
                const input = document.createElement('textarea');
                input.value = value;
                input.setAttribute('readonly', 'readonly');
                input.style.position = 'fixed';
                input.style.opacity = '0';
                document.body.appendChild(input);
                input.select();

                try {
                    document.execCommand('copy');
                    this.$toast.success('Clave copiada.', {icon: 'mdi-content-copy'});
                } catch (error) {
                    this.$toast.error('No se pudo copiar.', {icon: 'mdi-alert-circle'});
                } finally {
                    document.body.removeChild(input);
                }
            },
            deviceTrustColor(item) {
                if (item.status !== 'active') {
                    return 'grey';
                }

                return item.trusted && item.dynamic_key_enabled ? 'success' : 'primary';
            },
            deviceTrustLabel(item) {
                if (item.status !== 'active') {
                    return 'Retirado';
                }

                return item.trusted && item.dynamic_key_enabled ? 'Confiable' : 'Registrado';
            },
            openSecuritySection(key) {
                this.securityDialog = key;
                if (key === 'dynamic_key') {
                    this.refreshDynamicKey();
                }
            },
            closeSecuritySection() {
                this.securityDialog = null;
            },
            passwordConfirmationRule(value) {
                return value === this.form.password || 'La confirmación de la contraseña no coincide.';
            },
            getSecurity() {
                this.securityLoading = true;
                axios.get('/api/security', {
                    params: {
                        device_info: this.devicePayload()
                    }
                }).then(res => {
                    this.security = res.data || this.security;
                    this.setDynamicKey(this.security.account.dynamic_key || null);
                    this.loadPasskeys();
                }).catch(err => {
                    this.$toast.error(this.errorMessage(err, 'No fue posible cargar seguridad.'), {icon: 'mdi-alert-circle'});
                }).then(() => {
                    this.securityLoading = false;
                });
            },
            refreshDynamicKey() {
                if (this.dynamicKeyLoading) return;

                this.dynamicKeyLoading = true;
                axios.get('/api/security/dynamic-key', {
                    params: {
                        device_info: this.devicePayload()
                    }
                }).then(res => {
                    this.setDynamicKey(res.data.dynamic_key || null);
                }).catch(() => {
                    this.setDynamicKey(null);
                }).then(() => {
                    this.dynamicKeyLoading = false;
                });
            },
            setDynamicKey(payload) {
                this.dynamicKey = Object.assign({
                    enabled: false,
                    code: null,
                    period: 30,
                    remaining: 0,
                    expires_at: null,
                    device_name: null,
                    app_required: false,
                    manual_entry: false,
                    can_rotate: false,
                    rotation_expires_at: null,
                    rotation_due: false
                }, payload || {});

                if (this.dynamicKey.enabled && this.dynamicKey.expires_at) {
                    this.startDynamicKeyTimer();
                } else {
                    this.stopDynamicKeyTimer();
                }
            },
            startDynamicKeyTimer() {
                this.stopDynamicKeyTimer();
                this.tickDynamicKey();
                this.dynamicKeyTimer = setInterval(() => {
                    this.tickDynamicKey();
                }, 1000);
            },
            stopDynamicKeyTimer() {
                if (this.dynamicKeyTimer) {
                    clearInterval(this.dynamicKeyTimer);
                    this.dynamicKeyTimer = null;
                }
            },
            tickDynamicKey() {
                if (!this.dynamicKey.enabled || !this.dynamicKey.expires_at) return;

                const expiresAt = Date.parse(this.dynamicKey.expires_at);
                if (!expiresAt) return;

                const remaining = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
                this.$set(this.dynamicKey, 'remaining', remaining);

                if (remaining <= 1) {
                    this.refreshDynamicKey();
                }
            },
            forgetDevice(item) {
                this.$swal({
                    title: '¿Quitar este dispositivo?',
                    type: 'warning',
                    confirmButtonText: 'Si, quitar',
                    cancelButtonText: 'Cancelar'
                }).then((result) => {
                    if (!result.value) return;

                    this.revokingDeviceId = item.id;
                    axios.delete('/api/security/devices/' + item.id).then(res => {
                        this.$toast.success(res.data.status, {icon: 'mdi-check-circle'});
                        this.getSecurity();
                    }).catch(err => {
                        this.$toast.error(this.errorMessage(err, 'No fue posible quitar el dispositivo.'), {icon: 'mdi-alert-circle'});
                    }).then(() => {
                        this.revokingDeviceId = null;
                    });
                });
            },
            enableEmailTwoFactor() {
                this.twoFactorLoading = 'email';
                axios.post('/api/security/2fa/email/enable').then(res => {
                    this.$toast.success(res.data.status, {icon: 'mdi-check-circle'});
                    this.setTwoFactorStatus(res.data.two_factor);
                }).catch(err => {
                    this.handleErrors(err.response && err.response.data ? err.response.data.errors : {});
                    this.$toast.error(this.errorMessage(err, 'No fue posible activar el codigo al correo.'), {icon: 'mdi-alert-circle'});
                }).then(() => {
                    this.twoFactorLoading = null;
                });
            },
            setupAuthenticator() {
                this.twoFactorLoading = 'authenticator';
                axios.post('/api/security/2fa/authenticator/setup').then(res => {
                    this.authenticatorSetup = Object.assign({
                        secret: null,
                        otpauth_uri: null,
                        code: null
                    }, res.data.setup || {});
                    this.authenticatorDialog = true;
                    this.$toast.info(res.data.status, {icon: 'mdi-information'});
                }).catch(err => {
                    this.$toast.error(this.errorMessage(err, 'No fue posible preparar la app autenticadora.'), {icon: 'mdi-alert-circle'});
                }).then(() => {
                    this.twoFactorLoading = null;
                });
            },
            confirmAuthenticator() {
                if (!this.authenticatorSetup.code) return;

                this.twoFactorLoading = 'confirm-authenticator';
                axios.post('/api/security/2fa/authenticator/confirm', {
                    code: this.authenticatorSetup.code
                }).then(res => {
                    this.$toast.success(res.data.status, {icon: 'mdi-check-circle'});
                    this.setTwoFactorStatus(res.data.two_factor);
                    this.authenticatorDialog = false;
                    this.authenticatorSetup.code = null;
                }).catch(err => {
                    this.handleErrors(err.response && err.response.data ? err.response.data.errors : {});
                    this.$toast.error(this.errorMessage(err, 'No fue posible confirmar la app autenticadora.'), {icon: 'mdi-alert-circle'});
                }).then(() => {
                    this.twoFactorLoading = null;
                });
            },
            enableDynamicKey() {
                if (!this.nativeDynamicKeyDevice) {
                    this.$toast.info('La clave dinámica se activa desde la app Valzum instalada.', {icon: 'mdi-cellphone-lock'});
                    return;
                }

                this.twoFactorLoading = 'dynamic-key';
                axios.post('/api/security/dynamic-key/enable', {
                    period: 30,
                    device_info: this.devicePayload()
                }).then(res => {
                    this.$toast.success(res.data.status, {icon: 'mdi-check-circle'});
                    this.setDynamicKey(res.data.dynamic_key || null);
                    this.setTwoFactorStatus(res.data.two_factor);
                    this.getSecurity();
                }).catch(err => {
                    this.handleErrors(err.response && err.response.data ? err.response.data.errors : {});
                    this.$toast.error(this.errorMessage(err, 'No fue posible activar la clave dinámica.'), {icon: 'mdi-alert-circle'});
                }).then(() => {
                    this.twoFactorLoading = null;
                });
            },
            disableDynamicKey() {
                if (!this.nativeDynamicKeyDevice) {
                    this.$toast.info('La clave dinámica se administra desde la app Valzum instalada.', {icon: 'mdi-cellphone-lock'});
                    return;
                }

                this.twoFactorLoading = 'dynamic-key';
                axios.post('/api/security/dynamic-key/disable', {
                    device_info: this.devicePayload()
                }).then(res => {
                    this.$toast.success(res.data.status, {icon: 'mdi-check-circle'});
                    this.setDynamicKey(res.data.dynamic_key || null);
                    this.setTwoFactorStatus(res.data.two_factor);
                    this.getSecurity();
                }).catch(err => {
                    this.$toast.error(this.errorMessage(err, 'No fue posible desactivar la clave dinámica.'), {icon: 'mdi-alert-circle'});
                }).then(() => {
                    this.twoFactorLoading = null;
                });
            },
            rotateDynamicKey() {
                if (!this.nativeDynamicKeyDevice) {
                    this.$toast.info('La clave dinámica se cambia desde la app Valzum instalada.', {icon: 'mdi-cellphone-lock'});
                    return;
                }

                this.twoFactorLoading = 'dynamic-key-rotate';
                axios.post('/api/security/dynamic-key/rotate', {
                    rotation_days: 90,
                    device_info: this.devicePayload()
                }).then(res => {
                    this.$toast.success(res.data.status, {icon: 'mdi-check-circle'});
                    this.setDynamicKey(res.data.dynamic_key || null);
                    this.setTwoFactorStatus(res.data.two_factor);
                    this.getSecurity();
                }).catch(err => {
                    this.handleErrors(err.response && err.response.data ? err.response.data.errors : {});
                    this.$toast.error(this.errorMessage(err, 'No fue posible cambiar la clave dinámica.'), {icon: 'mdi-alert-circle'});
                }).then(() => {
                    this.twoFactorLoading = null;
                });
            },
            loadPasskeys() {
                if (!this.passkeySupported) return;

                axios.get('/api/security/passkeys').then(res => {
                    this.passkeys = res.data.passkeys || [];
                    if (res.data.passkey) {
                        this.$set(this.security.account.two_factor, 'passkey', !!res.data.passkey.enabled);
                        this.$set(this.security.account.two_factor, 'passkeys', Number(res.data.passkey.count || 0));
                    }
                }).catch(() => {});
            },
            openPasskeyDialog() {
                this.passkeyForm.password = null;
                this.passkeyDialog = true;
            },
            async createPasskey() {
                if (!this.passkeySupported || !this.passkeyForm.password || this.passkeyLoading) return;

                this.passkeyLoading = 'create';
                try {
                    const options = await axios.post('/api/security/passkeys/options', {
                        name: this.passkeyForm.name,
                        password: this.passkeyForm.password
                    });
                    const credential = await createPasskey(options.data.publicKey);
                    const verified = await axios.post('/api/security/passkeys/verify', {
                        challenge_id: options.data.challenge_id,
                        credential,
                        name: this.passkeyForm.name
                    });

                    this.$toast.success(verified.data.status || 'Llave de acceso activa.', {icon: 'mdi-check-circle'});
                    this.passkeys = verified.data.passkeys || [];
                    this.setTwoFactorStatus(verified.data.two_factor);
                    this.passkeyDialog = false;
                    this.passkeyForm.password = null;
                    this.getSecurity();
                } catch (err) {
                    this.handleErrors(err.response && err.response.data ? err.response.data.errors : {});
                    this.$toast.error(this.errorMessage(err, 'No fue posible crear la llave de acceso.'), {icon: 'mdi-alert-circle'});
                } finally {
                    this.passkeyLoading = null;
                }
            },
            deletePasskey(item) {
                this.$swal({
                    title: '¿Eliminar esta llave de acceso?',
                    type: 'warning',
                    confirmButtonText: 'Si, eliminar',
                    cancelButtonText: 'Cancelar'
                }).then((result) => {
                    if (!result.value) return;

                    this.passkeyLoading = 'delete-' + item.id;
                    axios.delete('/api/security/passkeys/' + item.id).then(res => {
                        this.$toast.success(res.data.status, {icon: 'mdi-check-circle'});
                        this.passkeys = res.data.passkeys || [];
                        this.setTwoFactorStatus(res.data.two_factor);
                        this.getSecurity();
                    }).catch(err => {
                        this.$toast.error(this.errorMessage(err, 'No fue posible eliminar la llave de acceso.'), {icon: 'mdi-alert-circle'});
                    }).then(() => {
                        this.passkeyLoading = null;
                    });
                });
            },
            disableTwoFactor() {
                if (!this.disableTwoFactorForm.password) return;

                this.twoFactorLoading = 'disable';
                axios.post('/api/security/2fa/disable', this.disableTwoFactorForm).then(res => {
                    this.$toast.success(res.data.status, {icon: 'mdi-check-circle'});
                    this.setTwoFactorStatus(res.data.two_factor);
                    this.setDynamicKey(null);
                    this.disableTwoFactorDialog = false;
                    this.disableTwoFactorForm.password = null;
                }).catch(err => {
                    this.$toast.error(this.errorMessage(err, 'No fue posible desactivar la verificacion.'), {icon: 'mdi-alert-circle'});
                }).then(() => {
                    this.twoFactorLoading = null;
                });
            },
            setTwoFactorStatus(status) {
                this.security.account.two_factor = status || this.twoFactorStatus;
            },
            changePass() {
                if (this.$refs.formPass.validate()) {
                    this.loading = true;

                    let formData = new FormData();

                    formData.append('old_password', this.form.old_password);
                    formData.append('password', this.form.password);
                    formData.append('password_confirmation', this.form.password_confirmation);

                    axios.post('/api/password/update', formData)
                        .then((res) => {
                            if (res.data.v === 'info') {
                                this.$toast.warning(res.data.status, {icon: 'mdi-information'});
                            } else {
                                this.$toast.success('Tu contraseña se actualizó correctamente.', {icon: 'mdi-check-circle'});
                                this.$store.dispatch('setUser', res.data.user || res.data);
                                this.$emit('success');
                                this.form.old_password = null;
                                this.form.password = null;
                                this.form.password_confirmation = null;
                                this.$refs.formPass.reset();
                            }
                        })
                        .catch(err => {
                            if (err.response && err.response.data && err.response.data.errors) {
                                this.handleErrors(err.response.data.errors);
                            } else {
                                this.$toast.error(this.errorMessage(err, 'No fue posible actualizar la contraseña.'), {icon: 'mdi-alert-circle'});
                            }
                        })
                        .then(() => {
                            this.loading = false;
                        });
                }
            },
            errorMessage(err, fallback) {
                if (err.response && err.response.data && err.response.data.status) {
                    return err.response.data.status;
                }

                return fallback;
            }
        }
    }
</script>

<style scoped>
.two-factor-option {
    min-height: 146px;
}

.security-inline-heading,
.security-section-card {
    border-color: rgba(13, 147, 165, 0.18);
}

.settings-panel-title {
    font-weight: 700;
    word-break: normal;
}

.security-section-card {
    cursor: pointer;
    transition: border-color .16s ease, box-shadow .16s ease, transform .16s ease;
}

.security-section-card:hover {
    transform: translateY(-2px);
}

.security-section-card-active {
    border-color: rgba(13, 147, 165, 0.65) !important;
    box-shadow: 0 0 0 1px rgba(13, 147, 165, 0.28);
}

.security-section-title {
    min-height: 40px;
}

.dynamic-key-code {
    letter-spacing: 0;
    font-variant-numeric: tabular-nums;
}

.dynamic-key-panel {
    cursor: pointer;
}

.security-fixed-dialog {
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}

.security-fixed-dialog-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}

.security-fixed-dialog-footer {
    flex: 0 0 auto;
}

@media (max-width: 600px) {
    .security-inline-heading {
        border-radius: 8px;
        margin-bottom: 8px !important;
    }

    .security-inline-heading .v-card__title {
        font-size: 1rem;
        line-height: 1.25;
        min-height: 48px;
        padding: 10px 12px;
        word-break: normal;
    }

    .security-section-card {
        border-radius: 8px;
    }

    .security-section-card:hover {
        transform: none;
    }

    .security-section-content {
        align-items: center;
        display: flex;
        min-height: 62px;
        padding: 10px 12px !important;
        text-align: left !important;
    }

    .security-section-content .v-avatar {
        height: 38px !important;
        margin-bottom: 0 !important;
        margin-right: 12px !important;
        min-width: 38px !important;
        width: 38px !important;
    }

    .security-section-title {
        flex: 1 1 auto;
        line-height: 1.2;
        min-height: 0;
        padding-right: 8px;
    }

    .security-section-content .v-chip {
        flex: 0 0 auto;
        margin-top: 0 !important;
        max-width: 110px;
    }

    .security-fixed-dialog {
        border-radius: 0 !important;
        max-height: 100vh;
    }

    .security-fixed-dialog .v-card__title {
        font-size: 1rem;
        line-height: 1.25;
        min-height: 52px;
        padding: 10px 12px;
        word-break: normal;
    }

    .security-fixed-dialog-body {
        padding: 12px !important;
    }

    .security-fixed-dialog-body .v-list-item {
        min-height: 56px;
        padding-left: 0;
        padding-right: 0;
    }

    .security-fixed-dialog-footer {
        padding: 8px 12px calc(10px + env(safe-area-inset-bottom));
    }
}
</style>
